import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ButtonBase,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  ContactMail as ContactIcon,
  LocalOffer as CouponIcon,
  BarChart as StatsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Apple as AppleIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { AdminUsers } from '../components/admin/AdminUsers';
import { AdminCoupons } from '../components/admin/AdminCoupons';
import { AdminContacts } from '../components/admin/AdminContacts';
import { AdminPrivacyRequests } from '../components/admin/AdminPrivacyRequests';
import { AdminSales } from '../components/admin/AdminSales';
import { AdminAuthGuard } from '../components/admin/AdminAuthGuard';
import { AdminStats } from '../components/admin/AdminStats';
import { AdminScheduledNotifications } from '../components/admin/AdminScheduledNotifications';
import { AdminAppleSubscriptions } from '../components/admin/AdminAppleSubscriptions';
import { AdminAppleOfferCodes } from '../components/admin/AdminAppleOfferCodes';
import { AdminAffiliates } from '../components/admin/AdminAffiliates';
import { AdminAppleSales } from '../components/admin/AdminAppleSales';

export const AdminPanel: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/');
  };

  // Índices mantêm o mapeamento original dos painéis
  const labels = ['Visão Geral', 'Usuários', 'Contatos', 'Privacidade', 'Cupons', 'Estatísticas', 'Notificações', 'Apple'];

  const groups = [
    {
      title: 'Principal',
      items: [
        { i: 0, label: 'Visão Geral', icon: <DashboardIcon /> },
        { i: 1, label: 'Usuários', icon: <PeopleIcon /> },
        { i: 5, label: 'Estatísticas', icon: <StatsIcon /> },
      ],
    },
    {
      title: 'Monetização',
      items: [
        { i: 4, label: 'Cupons', icon: <CouponIcon /> },
        { i: 7, label: 'Apple', icon: <AppleIcon /> },
      ],
    },
    {
      title: 'Suporte',
      items: [
        { i: 2, label: 'Contatos', icon: <ContactIcon /> },
        { i: 6, label: 'Notificações', icon: <NotificationsActiveIcon /> },
        { i: 3, label: 'Privacidade', icon: <SecurityIcon /> },
      ],
    },
  ];

  const panels = [
    <AdminSales />,
    <AdminUsers />,
    <AdminContacts />,
    <AdminPrivacyRequests />,
    <AdminCoupons />,
    <AdminStats />,
    <AdminScheduledNotifications />,
    <Box>
      <AdminAppleOfferCodes />
      <Divider sx={{ my: 6 }} />
      <AdminAffiliates />
      <Divider sx={{ my: 6 }} />
      <AdminAppleSales />
      <Divider sx={{ my: 6 }} />
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, backgroundColor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography sx={{ fontWeight: 700, color: theme.colors.text.secondary }}>
            Avançado · vincular compra manualmente
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <AdminAppleSubscriptions />
        </AccordionDetails>
      </Accordion>
    </Box>,
  ];

  const navItem = (item: { i: number; label: string; icon: React.ReactNode }) => {
    const selected = current === item.i;
    return (
      <ListItemButton
        key={item.i}
        selected={selected}
        aria-current={selected ? 'page' : undefined}
        onClick={() => setCurrent(item.i)}
        sx={{
          borderRadius: theme.radii.medium,
          minHeight: 44,
          px: 1.5,
          mb: 0.25,
          color: selected ? theme.colors.text.primary : theme.colors.text.secondary,
          backgroundColor: selected ? theme.colors.background : 'transparent',
          boxShadow: selected ? theme.shadows.small : 'none',
          '&:hover': { backgroundColor: selected ? theme.colors.background : `${theme.colors.primary}0d` },
        }}
      >
        <ListItemIcon sx={{ minWidth: 34, color: selected ? theme.colors.primary : 'inherit' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: '0.92rem', whiteSpace: 'nowrap' }}
        />
      </ListItemButton>
    );
  };

  return (
    <AdminAuthGuard>
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.colors.card }}>
        {/* Sidebar */}
        <Box
          component="nav"
          sx={{
            width: 264,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            backgroundColor: theme.colors.card,
            borderRight: `1px solid ${theme.colors.border}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            p: 2,
          }}
        >
          {/* Marca */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1, mb: 2 }}>
            <Box component="img" src="/HostlyIcon.svg" alt="Hostly" sx={{ width: 34, height: 34, borderRadius: theme.radii.small }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: theme.colors.text.primary, lineHeight: 1 }}>Hostly</Typography>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Admin</Typography>
            </Box>
          </Box>

          {/* Grupos de navegação */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {groups.map(group => (
              <Box key={group.title} sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ px: 1.5, color: theme.colors.text.secondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem' }}
                >
                  {group.title}
                </Typography>
                <List disablePadding sx={{ mt: 0.5 }}>
                  {group.items.map(navItem)}
                </List>
              </Box>
            ))}
          </Box>

          {/* Rodapé */}
          <Box
            sx={{
              mt: 1, p: 1.75, borderRadius: theme.radii.medium,
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.text.secondary})`,
              color: '#fff',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Abrir o app</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 1 }}>Ver o Hostly em produção</Typography>
            <ButtonBase
              onClick={() => navigate('/')}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', fontWeight: 700, borderRadius: 1, px: 0.5, py: 0.25 }}
            >
              Ir agora <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden="true" />
            </ButtonBase>
          </Box>
        </Box>

        {/* Área principal */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Topbar do conteúdo */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: { xs: 2, md: 4 }, py: 2,
              borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
              position: 'sticky', top: 0, zIndex: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.colors.text.secondary }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Painel</Typography>
              <ChevronRightIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              <Typography
                component="h1"
                variant="body2"
                sx={{ fontWeight: 700, color: theme.colors.text.primary }}
              >
                {labels[current]}
              </Typography>
            </Box>
            <Tooltip title="Sair do admin">
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{ backgroundColor: 'rgba(211,47,47,0.1)', color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211,47,47,0.2)' } }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Nav mobile (linha rolável) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' }, gap: 1, px: 2, py: 1.5,
              overflowX: 'auto', borderBottom: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.background,
            }}
          >
            {groups.flatMap(g => g.items).sort((a, b) => a.i - b.i).map(item => (
              <ButtonBase
                key={item.i}
                onClick={() => setCurrent(item.i)}
                aria-current={current === item.i ? 'page' : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75, flexShrink: 0,
                  minHeight: 40,
                  borderRadius: theme.radii.pill, fontSize: '0.85rem', fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: current === item.i ? '#fff' : theme.colors.text.secondary,
                  backgroundColor: current === item.i ? theme.colors.primary : theme.colors.card,
                }}
              >
                {item.label}
              </ButtonBase>
            ))}
          </Box>

          {/* Conteúdo */}
          <Box component="main" sx={{ flex: 1, backgroundColor: theme.colors.background, px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
            {panels[current]}
          </Box>
        </Box>
      </Box>
    </AdminAuthGuard>
  );
};
